import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useMoney } from '@/lib/hooks/useMoney'
import type { CalculatePriceResponse } from '@/types/checkout'

export interface SummaryAddOn {
  productId: string
  title: string
  amount: number
}

interface PriceSummaryProps {
  /** What is being bought, e.g. "Gold · Annual". */
  lineLabel: string
  /** The product's list price, shown while the quote loads. */
  listPrice: number
  quote: CalculatePriceResponse | null
  loading: boolean
  /** The total to charge, or null when it cannot be known (see `usePurchaseFlow`). */
  totalAmount: number | null
  currency: string
  installments?: number
  /** An annual option (F-36): the line shows the amount of each installment. */
  showInstallmentAmounts?: boolean
  /** F-43: the installment interest the buyer pays, its own line in the summary. */
  interest?: { amount: number; percent: string } | null
  /** The API's amount per installment (with interest), when it sent one. */
  installmentAmount?: number | null
  /** Add-ons bought with the plan (F-37): one line each, at the catalog amount until the quote itemizes them. */
  addOns?: SummaryAddOn[]
  /** FIN-7: the applied coupon's code and its discount on the order; 0 without one. */
  couponCode?: string | null
  discountAmount?: number
  /** The quote request failed; with no total to fall back on, offer a retry. */
  failed?: boolean
  onRetry?: () => void
  className?: string
}

/**
 * Product, coupon discount (FIN-7), platform fee (only when the buyer pays it) and total.
 *
 * Everything here comes from `calculate-price`, which only ever exposes the
 * platform fee — never what the payment provider costs. The fee line is
 * labelled as the platform's, because that is what it is: it does not depend
 * on the payment method.
 */
export function PriceSummary({
  lineLabel,
  listPrice,
  quote,
  loading,
  totalAmount,
  currency,
  installments = 1,
  showInstallmentAmounts = false,
  interest = null,
  installmentAmount = null,
  addOns = [],
  couponCode = null,
  discountAmount = 0,
  failed = false,
  onRetry,
  className
}: PriceSummaryProps) {
  const { t, i18n } = useTranslation('checkout')
  const formatMoney = useMoney()

  const productAmount = quote ? parseFloat(quote.breakdown?.product ?? quote.basePrice) : listPrice
  /* With add-ons the quote itemizes: each line's own price, and the platform
     fee as the sum of the items' fees (only present when the buyer pays). */
  const items = quote?.items
  const itemFor = (productId: string) => items?.find((item) => item.productId === productId)
  const itemFees = items?.reduce((sum, item) => sum + (item.fees ? parseFloat(item.fees.total) || 0 : 0), 0)
  /* FIN-7: with add-ons, each line also shows its share of the coupon. */
  const itemDiscount = (productId: string) => {
    const amount = addOns.length > 0 && discountAmount > 0 ? parseFloat(itemFor(productId)?.discountAmount ?? '0') : 0
    return amount > 0 ? amount : 0
  }
  const productId = quote?.productId
  const fee = items ? itemFees ?? null : quote?.fees ? parseFloat(quote.breakdown?.fees ?? quote.fees.total) : null

  return (
    <div className={cn('space-y-2.5 border-t border-gray-200 pt-4', className)}>
      <SummaryRow label={lineLabel} amount={formatMoney(productAmount, currency)}>
        {productId && itemDiscount(productId) > 0 && <ItemDiscount amount={itemDiscount(productId)} currency={currency} />}
      </SummaryRow>

      {addOns.map((addOn) => {
        const item = itemFor(addOn.productId)
        const discount = itemDiscount(addOn.productId)
        return (
          <SummaryRow
            key={addOn.productId}
            label={`+ ${addOn.title}`}
            amount={formatMoney(item ? parseFloat(item.baseAmount ?? item.basePrice) : addOn.amount, currency)}
          >
            {discount > 0 && <ItemDiscount amount={discount} currency={currency} />}
          </SummaryRow>
        )
      })}

      {discountAmount > 0 && (
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="min-w-0 text-gray-600">
            {couponCode ? t('purchase.coupon.discountLineWithCode', { code: couponCode }) : t('purchase.coupon.discountLine')}
          </span>
          <span className="flex-shrink-0 font-medium text-green-700">−{formatMoney(discountAmount, currency)}</span>
        </div>
      )}

      {fee !== null && fee > 0 && (
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-gray-600">{t('purchase.platformFee')}</span>
          <span className="flex-shrink-0 font-medium text-gray-900">{formatMoney(fee, currency)}</span>
        </div>
      )}

      {interest && (
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-gray-600">
            {t('purchase.installmentInterestLine', { percent: Number(interest.percent).toLocaleString(i18n.language, { maximumFractionDigits: 2 }) })}
          </span>
          <span className="flex-shrink-0 font-medium text-gray-900">{formatMoney(interest.amount, currency)}</span>
        </div>
      )}

      <SummaryTotal label={t('total')} amount={totalAmount !== null ? formatMoney(totalAmount, currency) : null} loading={loading} />

      {!loading && totalAmount !== null && installments > 1 && (
        <p className="text-right text-xs text-gray-500">
          {t(showInstallmentAmounts ? 'purchase.installmentOptionAmount' : 'purchase.installmentsOf', {
            count: installments,
            amount: formatMoney(installmentAmount ?? totalAmount / installments, currency)
          })}
        </p>
      )}

      {!loading && failed && totalAmount === null && onRetry && (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
          <span className="flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {t('purchase.priceUnavailable')}
          </span>
          <button type="button" onClick={onRetry} className="flex-shrink-0 font-medium underline">
            {t('purchase.tryAgain')}
          </button>
        </div>
      )}
    </div>
  )
}

/** One item's share of the coupon, under its line (FIN-7, only with add-ons). */
function ItemDiscount({ amount, currency }: { amount: number; currency: string }) {
  const { t } = useTranslation('checkout')
  const formatMoney = useMoney()
  return (
    <p className="mt-0.5 text-right text-xs text-green-700">
      {t('purchase.coupon.itemDiscount', { amount: formatMoney(amount, currency) })}
    </p>
  )
}

/**
 * One line of an order summary: what it is on the left, its amount on the right, an optional note below.
 * Shared by the checkout's `PriceSummary` and the plan change summary (F-195).
 */
export function SummaryRow({ label, amount, hint, testId, children }: { label: ReactNode; amount: ReactNode; hint?: ReactNode; testId?: string; children?: ReactNode }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-start justify-between gap-3 text-sm">
        <span className="min-w-0 break-words text-gray-600">{label}</span>
        <span className="flex-shrink-0 font-medium text-gray-900">{amount}</span>
      </div>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
      {children}
    </div>
  )
}

/** The order's total, highlighted, right above the button: "—" when it cannot be known. */
export function SummaryTotal({ label, amount, loading = false, testId }: { label: ReactNode; amount: ReactNode | null; loading?: boolean; testId?: string }) {
  const { t } = useTranslation('checkout')
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-gray-200 pt-2.5" data-testid={testId}>
      <span className="text-base font-semibold text-gray-900">{label}</span>
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-label={t('purchase.calculating')} />
      ) : amount !== null ? (
        <span className="text-xl font-bold text-primary">{amount}</span>
      ) : (
        <span className="text-sm text-gray-500">—</span>
      )}
    </div>
  )
}
